import { useRequest } from 'features/request/useRequest';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { catalogOtherCostVariablesGql } from 'pages/Catalogs/Common/CatalogTable/catalogQueries';

import type { IGetRowsParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';

interface IUseCatalogVariablesOtherData {
    catalogId: string | undefined;
    globalOnly: boolean;
    methodology: ProjectMethodologyEnum;
    filterValues: Record<string, string[]>;
}

const getWhereVariables = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    const predicates = Object.entries(filterValues).reduce(
        (acc: Array<Record<string, { in: string[] }>>, [key, value]) => {
            if (value.length) {
                acc.push({
                    [key]: { in: value },
                });
            }

            return acc;
        },
        [],
    );

    return predicates.length ? { and: predicates } : undefined;
};

export const useCatalogVariablesOtherData = ({
    catalogId,
    globalOnly,
    methodology,
    filterValues,
}: IUseCatalogVariablesOtherData) => {
    const requestId = `catalogOtherCostVariables_${catalogId}_${methodology}_${globalOnly}`;

    const [catalogOtherCostVariablesResponse, , , catalogOtherCostVariablesMakeRequest] = useRequest({
        requestId,
        query: catalogOtherCostVariablesGql,
        withEffect: false,
    });

    const variables = useMemo(
        () =>
            catalogId
                ? {
                      catalogUniqueId: catalogId,
                      globalOnly,
                      methodology,
                      where: getWhereVariables(filterValues),
                  }
                : undefined,
        [catalogId, filterValues, globalOnly, methodology],
    );

    const changeOrderHandler = useCallback((params: IGetRowsParams) => {
        const { sortModel } = params;
        const { sort, colId } = sortModel?.[0] ?? {};

        if (colId && sort && colId !== 'dataSourceOrFormula') {
            return [{ [colId]: sort.toUpperCase() }];
        }

        return undefined;
    }, []);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: catalogOtherCostVariablesMakeRequest,
        requestVariables: variables,
        customSortFn: changeOrderHandler,
    });

    useEffect(() => {
        if (catalogOtherCostVariablesResponse?.catalogOtherCostVariables && getRowParams?.current) {
            getRowParams.current.successCallback(
                catalogOtherCostVariablesResponse.catalogOtherCostVariables?.items ?? [],
                catalogOtherCostVariablesResponse.catalogOtherCostVariables?.totalCount,
            );
        }
    }, [
        catalogOtherCostVariablesResponse?.catalogOtherCostVariables?.items,
        catalogOtherCostVariablesResponse?.catalogOtherCostVariables?.totalCount,
        catalogOtherCostVariablesResponse?.catalogOtherCostVariables,
        filterValues,
        getRowParams,
    ]);

    const totalCount = catalogOtherCostVariablesResponse?.catalogOtherCostVariables?.totalCount ?? 0;

    const fetchedCount = useMemo<number>(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams.current.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams.current.endRow;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalCount, getRowParams?.current?.endRow]);

    return useMemo(
        () => ({
            datasource: dataSource,
            totalCount,
            fetchedCount,
        }),
        [dataSource, fetchedCount, totalCount],
    );
};
